import React, { useState, useEffect, useContext } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import FreezerTitle from '../include/FreezerTitle'
import { Store } from '../../Utils/Store'
import ServicesSidebar from '../../Components/ServicesSidebar'

const DynamicServiceDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { state } = useContext(Store)
  const [subcategory, setSubcategory] = useState(null)
  const [category, setCategory] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    // Find subcategory by ID (handle both string and numeric IDs)
    const foundSubcategory = state.Subcategories.find(sub => String(sub.id) === String(id))
    
    if (foundSubcategory) {
      setSubcategory(foundSubcategory)
      // Find category (handle both string and numeric IDs)
      const foundCategory = state.Categories.find(cat => String(cat.id) === String(foundSubcategory.category_id))
      if (foundCategory) {
        setCategory(foundCategory)
      }
    } else {
      toast.error('Service not found')
      navigate('/services-overview')
    }
    
    setLoading(false)
  }, [id, state.Subcategories, state.Categories, navigate])

  if (loading) {
    return (
      <div className="page-content">
        <div className="container">
          <div className="row">
            <div className="col-md-12">
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!subcategory) {
    return null
  }

  // Generate SEO-friendly keywords based on service type with Dubai focus
  const getServiceKeywords = (serviceName) => {
    const name = serviceName.toLowerCase()
    const baseKeywords = `${serviceName} Dubai, ${serviceName} UAE, best ${serviceName} Dubai, ${serviceName} services Dubai, ${serviceName} Abu Dhabi, ${serviceName} Sharjah`
    
    if (name.includes('freezer')) {
      return `${baseKeywords}, freezer transportation Dubai, freezer van Dubai, freezer truck Dubai, frozen goods transport Dubai, cold chain logistics Dubai, freezer transportation UAE, freezer van services Dubai, best freezer transport Dubai, top freezer company Dubai, professional freezer transport Dubai`
    } else if (name.includes('chiller')) {
      return `${baseKeywords}, chiller transportation Dubai, chiller truck Dubai, chiller van Dubai, temperature controlled transport Dubai, perishable goods transport Dubai, chiller transportation UAE, chiller truck services Dubai, best chiller transport Dubai, top chiller company Dubai, professional chiller transport Dubai`
    } else if (name.includes('mover') || name.includes('moving')) {
      return `${baseKeywords}, mover transportation Dubai, moving services Dubai, relocation services Dubai, home moving Dubai, office relocation Dubai, mover transportation UAE, best moving company Dubai, top movers Dubai, professional movers Dubai, reliable movers Dubai`
    } else if (name.includes('trailer')) {
      return `${baseKeywords}, trailer transportation Dubai, trailer services Dubai, heavy duty trailer Dubai, cotton box trailer Dubai, flat bed trailer Dubai, trailer transportation UAE, best trailer services Dubai, top trailer company Dubai, professional trailer transport Dubai`
    }
    return `${baseKeywords}, transportation services Dubai, transport services Dubai, logistics Dubai, best transport Dubai, top transport company Dubai`
  }

  const serviceKeywords = getServiceKeywords(subcategory.name)
  const seoTitle = `${subcategory.name} Dubai | Best ${category?.name || 'Transportation'} Services in Dubai, UAE - KD General Transport`
  const seoDescription = `Best ${subcategory.name} services in Dubai, UAE by KD General Transport. Professional ${subcategory.name} in Dubai serving Downtown Dubai, JBR, Business Bay, Dubai Marina & All Areas. ${category?.name || 'Transportation'} services with modern fleet, experienced team, and 24/7 support. Call +971555592206`

  return (
    <div>
      <FreezerTitle 
        title={subcategory.name}
        breadcrumbItems={[
          { label: "Home", path: "/" },
          { label: "Services", path: "/services-overview" },
          { label: category?.name || "Service", path: "#" },
          { label: subcategory.name, path: "#" }
        ]}
        seoTitle={seoTitle}
        seoDescription={seoDescription}
        seoKeywords={serviceKeywords}
        seoImage={subcategory.image || `https://kdgeneraltransport.com/img/cars/chiller2.jpeg`}
      />
      <div className="page-content">
        <div className="container">
          <div className="row">
            <aside className="col-md-3 aside aside-left">
              <ServicesSidebar currentSubcategoryId={id} currentCategoryId={subcategory?.category_id} />
              <ul className="aside-widgets">
                <li className="widget widget-text">
                  <div className="title">
                    <h3>contact us</h3>
                  </div>
                  <img src={`${process.env.PUBLIC_URL}/img/pics/locations.jpg`} alt="contact us" />
                  <br />
                  <p>Let us know what can we do for you. Contact us today!</p>
                  <Link to="/contact" className="read-more">
                    <span>
                      Contact us
                      <i className="fa fa-chevron-right" />
                    </span>
                  </Link>
                </li>
              </ul>
            </aside>
            <div className="col-md-9">
              <div className="service-image-container">
                <img 
                  src={subcategory.image || `${process.env.PUBLIC_URL}/img/cars/chiller2.jpeg`} 
                  alt={subcategory.name} 
                />
                <a 
                  href={`https://wa.me/971555592206?text=Hi%20KD%20General%20Transport!%20I%20want%20to%20book%20${encodeURIComponent(subcategory.name)}%20service.`}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="book-now-button"
                >
                  <i className="fa fa-whatsapp"></i>
                  Book Now
                </a>
              </div>
              <br />
              <div className="custom-heading">
                <h2>Professional {subcategory.name}</h2>
              </div>
              {subcategory.content ? (
                <div dangerouslySetInnerHTML={{ __html: subcategory.content.replace(/\n/g, '<br />') }} />
              ) : (
                <div>
                  <p>
                    KD General Transport provides professional {subcategory.name} services across the UAE, ensuring your goods are transported safely and efficiently.
                  </p>
                  <p>
                    Our experienced team and modern fleet are ready to serve you across all Emirates including Dubai, Abu Dhabi, Sharjah, Ajman, Umm Al Quwain, Ras Al Khaimah, and Fujairah.
                  </p>
                </div>
              )}
              <br />
              <br />
              <h3>Your benefits</h3>
              <p>
                When you choose KD General Transport's {subcategory.name} services, you benefit from our commitment to excellence in transportation and logistics.
              </p>
              <br />
              <img
                className="float-right"
                width={360}
                src={`${process.env.PUBLIC_URL}/img/pics/img25.png`}
                alt="Benefits"
              />
              <ul className="fa-ul">
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Professional and experienced team
                </li>
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Modern and well-maintained fleet
                </li>
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Coverage across all Emirates
                </li>
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Flexible scheduling options
                </li>
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Fully insured vehicles
                </li>
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Competitive pricing
                </li>
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Reliable and timely delivery
                </li>
                <li>
                  <i className="fa fa-li fa-long-arrow-right" />
                  Customer-focused service
                </li>
              </ul>
              <br />
              <br />
              <div className="custom-heading">
                <h3>industries covered</h3>
              </div>
              <ul className="service-list-big-icons clearfix">
                <li>
                  <div className="icon-container">
                    <img src={`${process.env.PUBLIC_URL}/img/svg/pi-food-beverage.svg`} alt="food and beverage svg icon" />
                  </div>
                  <h4>Food &amp; Beverage</h4>
                </li>
                <li>
                  <div className="icon-container">
                    <img
                      src={`${process.env.PUBLIC_URL}/img/svg/pi-cargo-retail.svg`}
                      alt="retail svg icon"
                    />
                  </div>
                  <h4>Retail &amp; Supermarkets</h4>
                </li>
                <li>
                  <div className="icon-container">
                    <img src={`${process.env.PUBLIC_URL}/img/svg/pi-mark-energy.svg`} alt="pharmaceutical svg icon" />
                  </div>
                  <h4>Pharmaceutical</h4>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicServiceDetail
